import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  GripVertical,
  Plus,
  Trash2,
  Edit,
  Save,
  X,
  Loader2,
  Menu,
  ExternalLink,
  Link as LinkIcon,
} from 'lucide-react';

const SortableNavItem = ({ item, onEdit, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: item.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 p-3 bg-white rounded-lg border border-[#f8fafc] hover:border-[#20acf6]/30 transition-colors"
      data-testid={`nav-item-${item.id}`}
    >
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-[#02d1d4] hover:text-[#20acf6] p-1"
        data-testid={`drag-handle-${item.id}`}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm text-[#1e293b] truncate">{item.label}</span>
          {item.is_external && <ExternalLink className="h-3 w-3 text-[#02d1d4] flex-shrink-0" />}
        </div>
        <span className="text-xs text-[#1e293b]/40 truncate block">{item.url}</span>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        <Button variant="ghost" size="icon" onClick={() => onEdit(item)} className="h-8 w-8">
          <Edit className="h-3.5 w-3.5 text-[#1e293b]/50" />
        </Button>
        <Button variant="ghost" size="icon" onClick={() => onDelete(item.id)} className="h-8 w-8">
          <Trash2 className="h-3.5 w-3.5 text-[#F4A261]" />
        </Button>
      </div>
    </div>
  );
};

const NavigationManager = () => {
  const { api } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    label: '',
    url: '',
    position: 'header',
    is_external: false,
    open_in_new_tab: false,
  });

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const res = await api.get('/cms/navigation/all');
      setItems(res.data);
    } catch (error) {
      console.error('Failed to fetch navigation items:', error);
    } finally {
      setLoading(false);
    }
  };

  const headerItems = items.filter(i => i.position === 'header');
  const footerItems = items.filter(i => i.position === 'footer');

  const handleDragEnd = async (event, position) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const positionItems = position === 'header' ? headerItems : footerItems;
    const oldIndex = positionItems.findIndex(i => i.id === active.id);
    const newIndex = positionItems.findIndex(i => i.id === over.id);
    const reordered = arrayMove(positionItems, oldIndex, newIndex);

    const otherItems = items.filter(i => i.position !== position);
    setItems([...otherItems, ...reordered]);

    try {
      await api.post('/cms/navigation/reorder', reordered.map((item, idx) => ({ id: item.id, order: idx })));
      toast.success('Navigation order saved');
    } catch (error) {
      toast.error('Failed to save order');
      fetchItems();
    }
  };

  const resetForm = () => {
    setFormData({ label: '', url: '', position: 'header', is_external: false, open_in_new_tab: false });
    setEditingItem(null);
    setShowForm(false);
  };

  const handleEdit = (item) => {
    setFormData({
      label: item.label,
      url: item.url,
      position: item.position,
      is_external: item.is_external,
      open_in_new_tab: item.open_in_new_tab,
    });
    setEditingItem(item);
    setShowForm(true);
  };

  const handleSubmit = async () => {
    if (!formData.label.trim() || !formData.url.trim()) {
      toast.error('Label and URL are required');
      return;
    }
    setSaving(true);
    try {
      if (editingItem) {
        await api.put(`/cms/navigation/${editingItem.id}`, formData);
        toast.success('Link updated');
      } else {
        await api.post('/cms/navigation', { ...formData, order: items.length });
        toast.success('Link added');
      }
      resetForm();
      fetchItems();
    } catch (error) {
      toast.error('Failed to save link');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this navigation link?')) return;
    try {
      await api.delete(`/cms/navigation/${id}`);
      setItems(items.filter(i => i.id !== id));
      toast.success('Link deleted');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  if (loading) {
    return (
      <DashboardLayout requiredRoles={['super_admin']}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#20acf6]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout requiredRoles={['super_admin']}>
      <div className="space-y-6" data-testid="navigation-manager">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#2a3e5d]">Navigation</h1>
            <p className="text-sm text-[#1e293b]/60 mt-1">
              Manage header and footer navigation links. Drag to reorder.
            </p>
          </div>
          <Button
            onClick={() => { resetForm(); setShowForm(true); }}
            className="bg-[#20acf6] hover:bg-[#3bb8f7] text-white"
            data-testid="add-nav-item-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Link
          </Button>
        </div>

        {/* Add/Edit Form */}
        {showForm && (
          <Card className="border-[#20acf6]/30 bg-[#f8fafc]/30" data-testid="nav-item-form">
            <CardContent className="p-6 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm text-[#1e293b]">Label</Label>
                  <Input
                    value={formData.label}
                    onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                    placeholder="e.g. About Us"
                    className="border-[#f8fafc] focus:border-[#20acf6]"
                    data-testid="nav-label-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-[#1e293b]">URL</Label>
                  <Input
                    value={formData.url}
                    onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                    placeholder="/page/about-us or https://..."
                    className="border-[#f8fafc] focus:border-[#20acf6]"
                    data-testid="nav-url-input"
                  />
                </div>
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm text-[#1e293b]">Position</Label>
                  <Select value={formData.position} onValueChange={(v) => setFormData({ ...formData, position: v })}>
                    <SelectTrigger className="border-[#f8fafc]" data-testid="nav-position-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="header">Header</SelectItem>
                      <SelectItem value="footer">Footer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end gap-4">
                  <label className="flex items-center gap-2 text-sm text-[#1e293b] cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_external}
                      onChange={(e) => setFormData({ ...formData, is_external: e.target.checked })}
                      className="accent-[#20acf6]"
                    />
                    External link
                  </label>
                  <label className="flex items-center gap-2 text-sm text-[#1e293b] cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.open_in_new_tab}
                      onChange={(e) => setFormData({ ...formData, open_in_new_tab: e.target.checked })}
                      className="accent-[#20acf6]"
                    />
                    New tab
                  </label>
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={resetForm} className="border-[#f8fafc]">
                  <X className="h-4 w-4 mr-1" /> Cancel
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={saving}
                  className="bg-[#20acf6] hover:bg-[#3bb8f7] text-white"
                  data-testid="save-nav-item-btn"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
                  {editingItem ? 'Update' : 'Add'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Header Navigation */}
          <Card className="border-[#f8fafc]">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2 text-[#1e293b]">
                <Menu className="h-4 w-4 text-[#20acf6]" />
                Header Navigation
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              {headerItems.length === 0 ? (
                <p className="text-sm text-[#1e293b]/40 text-center py-6">No header links yet</p>
              ) : (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => handleDragEnd(e, 'header')}>
                  <SortableContext items={headerItems.map(i => i.id)} strategy={verticalListSortingStrategy}>
                    <div className="space-y-2" data-testid="header-nav-list">
                      {headerItems.map(item => (
                        <SortableNavItem key={item.id} item={item} onEdit={handleEdit} onDelete={handleDelete} />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}
            </CardContent>
          </Card>

          {/* Footer Navigation */}
          <Card className="border-[#f8fafc]">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2 text-[#1e293b]">
                <LinkIcon className="h-4 w-4 text-[#02d1d4]" />
                Footer Navigation
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              {footerItems.length === 0 ? (
                <p className="text-sm text-[#1e293b]/40 text-center py-6">No footer links yet</p>
              ) : (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => handleDragEnd(e, 'footer')}>
                  <SortableContext items={footerItems.map(i => i.id)} strategy={verticalListSortingStrategy}>
                    <div className="space-y-2" data-testid="footer-nav-list">
                      {footerItems.map(item => (
                        <SortableNavItem key={item.id} item={item} onEdit={handleEdit} onDelete={handleDelete} />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default NavigationManager;
